import React from "react";
import isPropValid from "@emotion/is-prop-valid";

// Props that @emotion/is-prop-valid incorrectly treats as valid HTML.
// These are React Native or component-specific props that match
// valid HTML patterns (on* event handlers, SVG attributes).
export const ADDITIONAL_BLOCKED_PROPS = new Set([
  // RN-only event handlers (pass isPropValid's on* pattern)
  "onPress",
  "onChangeText",
  "onLayout",
  "onMoveShouldSetResponder",
  "onResponderGrant",
  "onResponderMove",
  "onResponderRelease",
  "onResponderTerminate",
  // SVG attributes that pass isPropValid
  "strokeWidth",
  // CSS properties that pass isPropValid but are used as component props
  "overflow",
  "cursor",
  "fontSize",
  "fontWeight",
  "fontFamily",
  "textDecoration",
]);

function shouldForwardProp(key: string): boolean {
  if (ADDITIONAL_BLOCKED_PROPS.has(key)) return false;
  return isPropValid(key);
}

/**
 * Creates a React component that renders the given HTML tag
 * but filters out non-HTML props before they reach the DOM.
 *
 * Uses @emotion/is-prop-valid (same library styled-components v4
 * uses internally) to automatically block invalid HTML attributes,
 * plus a small blocklist for false positives (RN on* handlers, SVG attrs).
 *
 * Usage: `const FilteredDiv = createFilteredElement("div");`
 * Then:  `const StyledBox = styled(FilteredDiv)<BoxProps>\`...\`;`
 *
 * styled-components can still read ALL props for CSS interpolation,
 * but only valid HTML attributes are forwarded to the DOM element.
 */
export function createFilteredElement(defaultTag: string) {
  const Component = React.forwardRef<HTMLElement, Record<string, unknown>>(
    ({ children, elementType, ...props }, ref) => {
      const Tag = (elementType as string) || defaultTag;
      const htmlProps: Record<string, unknown> = {};
      for (const key of Object.keys(props)) {
        if (shouldForwardProp(key)) {
          htmlProps[key] = props[key];
        }
      }
      return React.createElement(
        Tag,
        { ref, ...htmlProps },
        children as React.ReactNode
      );
    }
  );
  Component.displayName = `Filtered(${defaultTag})`;
  return Component;
}
